<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.services' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.services") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("common.create") }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-200">
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">{{ $t('forms.service.create-title') }}</h1>
                    <p class="tw-text-sm tw-text-gray-600">{{ $t('forms.service.create-subtitle') }}</p>
                </div>
                
                <!-- Back Button -->
                <a-button
                    @click="goBack"
                    intent="light"
                    left-icon="solar:arrow-left-linear"
                >
                    {{ $t('common.back-to-services') }}
                </a-button>
            </div>

            <!-- Skeleton Loader -->
            <div v-if="categoriesLoading" class="tw-w-full">
                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                    <!-- Left Column - Form Skeleton -->
                    <div class="xl:tw-col-span-8 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-40 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div class="tw-space-y-6">
                                <div>
                                    <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-64 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                                <div>
                                    <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-24 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-72 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                                <div>
                                    <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-80 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                            </div>
                        </div>
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-36 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div>
                                <div class="tw-w-28 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                <div class="tw-w-56 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Right Column - Image Skeleton -->
                    <div class="xl:tw-col-span-4">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-28 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div class="tw-w-full tw-h-80 tw-bg-gray-200 tw-rounded-lg tw-mb-6"></div>
                            <div class="tw-bg-gray-100 tw-rounded-lg tw-p-4">
                                <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                <div class="tw-space-y-2">
                                    <div class="tw-w-full tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-5/6 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-4/5 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-3/4 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form Content -->
            <form v-else @submit.prevent="createService" class="tw-w-full">
                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                    <!-- Left Column - Service Information -->
                    <div class="xl:tw-col-span-8 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('forms.service.service-information') }}</h2>
                            
                            <div class="tw-space-y-6">
                                <!-- Service Name -->
                                <FormField
                                    v-model="serviceForm.name"
                                    type="text"
                                    :label="$t('forms.service.name')"
                                    :placeholder="$t('forms.service.name-placeholder')"
                                    left-icon="solar:settings-linear"
                                    :error="serviceErrors.name"
                                    required
                                    :help="$t('forms.service.name-help')"
                                />

                                <!-- Service Description -->
                                <FormField
                                    v-model="serviceForm.description"
                                    type="textarea"
                                    :label="$t('forms.service.description')"
                                    :placeholder="$t('forms.service.description-placeholder')"
                                    :error="serviceErrors.description"
                                    rows="6"
                                    :help="$t('forms.service.description-help')"
                                />

                                <!-- Categories -->
                                <FormMultiSelect
                                    v-model="serviceForm.categories"
                                    :options="categoryOptions"
                                    :label="$t('forms.service.categories')"
                                    :placeholder="$t('forms.service.categories-placeholder')"
                                    left-icon="solar:widget-bold-duotone"
                                    :error="serviceErrors.categories"
                                    :help="$t('forms.service.categories-help')"
                                    :loading="categoriesLoading"
                                />

                                <!-- Company -->
                                <FormVueSelect
                                    v-model="serviceForm.company_id"
                                    :options="companyOptions"
                                    option-value="id"
                                    option-label="name"
                                    :label="$t('forms.service.company', 'Company')"
                                    :placeholder="$t('forms.service.company-placeholder', 'Select company')"
                                    left-icon="solar:buildings-bold-duotone"
                                    :error="serviceErrors.company_id"
                                    :help="$t('forms.service.company-help', 'Select the company that provides this service')"
                                    :loading="companiesLoading"
                                    searchable
                                    clearable
                                />

                                <!-- Status -->
                                <FormSelect
                                    v-model="serviceForm.status"
                                    :options="statusOptions"
                                    :label="$t('forms.service.status')"
                                    left-icon="solar:shield-check-bold-duotone"
                                    :error="serviceErrors.status"
                                    :help="$t('forms.service.status-help')"
                                ></FormSelect>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Service Image -->
                    <div class="xl:tw-col-span-4 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('forms.service.service-image') }}</h2>
                            
                            <!-- Image Upload Area -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">
                                    {{ $t('forms.service.image-label') }} <span class="tw-text-gray-500">({{ $t('common.optional') }})</span>
                                </label>
                                
                                <div
                                    @drop.prevent="handleImageDrop"
                                    @dragover.prevent="isDragging = true"
                                    @dragleave.prevent="isDragging = false"
                                    @click="triggerImageInput"
                                    :class="[
                                        'tw-relative tw-w-full tw-h-80 tw-border-2 tw-border-dashed tw-rounded-lg tw-cursor-pointer tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                                        isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400 tw-bg-gray-50 hover:tw-bg-gray-100',
                                        serviceForm.imagePreview ? 'tw-border-solid tw-bg-white' : ''
                                    ]"
                                >
                                    <!-- Image Preview -->
                                    <div v-if="serviceForm.imagePreview" class="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                                        <img
                                            :src="serviceForm.imagePreview"
                                            alt="Service preview"
                                            class="tw-max-w-full tw-max-h-full tw-object-contain tw-rounded-lg"
                                        />
                                        <button
                                            @click.stop="removeImage"
                                            type="button"
                                            class="tw-absolute tw-top-3 tw-right-3 tw-w-10 tw-h-10 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors tw-shadow-lg"
                                        >
                                            <icon icon="solar:close-circle-bold" class="tw-text-xl" />
                                        </button>
                                    </div>
                                    
                                    <!-- Upload Placeholder - Centered -->
                                    <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-8">
                                        <div class="tw-mb-4">
                                            <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400" />
                                        </div>
                                        <h3 class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                            {{ $t('forms.service.upload-image') }}
                                        </h3>
                                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4 tw-max-w-xs">
                                            {{ $t('forms.service.upload-description') }}
                                        </p>
                                        <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-500 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-primary-600 tw-transition-colors">
                                            <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                            {{ $t('forms.service.choose-image') }}
                                        </div>
                                        <p class="tw-text-xs tw-text-gray-400 tw-mt-3">
                                            {{ $t('forms.service.file-format') }}
                                        </p>
                                    </div>
                                    
                                    <input
                                        ref="imageInput"
                                        type="file"
                                        accept="image/*"
                                        @change="handleImageSelect"
                                        class="tw-hidden"
                                    />
                                </div>
                                
                                <p v-if="serviceErrors.image" class="tw-text-red-500 tw-text-sm tw-mt-2">
                                    {{ serviceErrors.image }}
                                </p>
                            </div>

                            <!-- Image Upload Tips -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2 tw-flex tw-items-center">
                                    <icon icon="solar:info-circle-bold" class="tw-mr-2" />
                                    {{ $t('forms.service.image-tips') }}
                                </h3>
                                <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                    <li>• {{ $t('forms.service.image-tip-1') }}</li>
                                    <li>• {{ $t('forms.service.image-tip-2') }}</li>
                                    <li>• {{ $t('forms.service.image-tip-3') }}</li>
                                    <li>• {{ $t('forms.service.image-tip-4') }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-8">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Cancel Button -->
                        <a-button
                            type="button"
                            intent="white"
                            @click="goBack"
                            :disabled="savingService"
                        >
                            {{ $t('common.cancel') }}
                        </a-button>
                        
                        <!-- Create Service Button -->
                        <a-button
                            type="submit"
                            intent="primary"
                            :loading="savingService"
                            :disabled="!serviceForm.name || savingService"
                            left-icon="solar:add-circle-bold-duotone"
                            class="tw-min-w-[180px]"
                        >
                            {{ $t('forms.service.create-button') }}
                        </a-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import FormField from '@/components/form/FormField.vue'
import FormSelect from '@/components/form/FormSelect.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import FormVueSelect from '@/components/form/FormVueSelect.vue'
import useAlert from '@/composables/useAlert'
import * as serviceApi from '@/api/services'
import appApi from '@/api/app'
import companyApi from '@/api/companies'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()

// Form state
const savingService = ref(false)
const isDragging = ref(false)
const imageInput = ref(null)
const categoriesLoading = ref(false)
const categories = ref([])
const companiesLoading = ref(false)
const companies = ref([])

// Service form
const serviceForm = reactive({
    name: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: [],
    company_id: null, // No default selection - will show placeholder
    status: 'active'
})

const serviceErrors = reactive({
    name: '',
    description: '',
    image: '',
    categories: '',
    company_id: '',
    status: ''
})

// Image handling methods
const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        processSelectedImage(file)
    }
}

const processSelectedImage = (file) => {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image file size must be less than 5MB', 'warning')
        return
    }

    serviceForm.image = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        serviceForm.imagePreview = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    serviceForm.image = null
    serviceForm.imagePreview = ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

const companyOptions = computed(() => {
    const defaultOption = { id: null, name: 'Select Company' }
    const companyOptions = companies.value.map(company => ({
        id: company.id,
        name: company.name
    }))
    return [defaultOption, ...companyOptions]
})

const statusOptions = computed(() => {
    return [
        { id: 'active', name: t('forms.service.status-active') },
        { id: 'inactive', name: t('forms.service.status-inactive') }
    ]
})

// API Methods
const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('service')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        categories.value = []
        useAlert('Failed to load service categories', 'warning')
    } finally {
        categoriesLoading.value = false
    }
}

const fetchCompanies = async () => {
    companiesLoading.value = true
    
    try {
        const response = await companyApi.admin.all()
        companies.value = response.data?.companies || []
    } catch (error) {
        console.error('Error fetching companies:', error)
        companies.value = []
        useAlert('Failed to load companies', 'warning')
    } finally {
        companiesLoading.value = false
    }
}

// Methods
const goBack = () => {
    router.push({ name: 'pages.admin.services' })
}

const validateServiceForm = () => {
    // Reset errors
    Object.keys(serviceErrors).forEach(key => {
        serviceErrors[key] = ''
    })

    let isValid = true

    // Name validation
    if (!serviceForm.name.trim()) {
        serviceErrors.name = 'Service name is required'
        isValid = false
    } else if (serviceForm.name.trim().length < 3) {
        serviceErrors.name = 'Service name must be at least 3 characters'
        isValid = false
    } else if (serviceForm.name.trim().length > 100) {
        serviceErrors.name = 'Service name must be less than 100 characters'
        isValid = false
    }

    // Description validation
    if (serviceForm.description.trim().length > 1000) {
        serviceErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    // Company validation
    if (!serviceForm.company_id) {
        serviceErrors.company_id = 'Company selection is required'
        isValid = false
    }

    return isValid
}

const createService = async () => {
    if (!validateServiceForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingService.value = true

    try {
        // Create FormData for file upload
        const formData = new FormData()
        
        // Add required fields
        formData.append('name', serviceForm.name.trim())
        formData.append('description', serviceForm.description.trim())
        formData.append('company_id', serviceForm.company_id)
        formData.append('status', serviceForm.status)
        
        // Add image if selected
        if (serviceForm.image) {
            formData.append('image', serviceForm.image)
        }
        
        // Add categories as JSON array
        if (serviceForm.categories && serviceForm.categories.length > 0) {
            const categoryIds = serviceForm.categories.map(cat => cat.id)
            categoryIds.forEach(id => {
                formData.append('category_ids[]', id)
            })
        }

        // Call the API - use admin endpoint if available
        const response = serviceApi.admin && serviceApi.admin.create 
            ? await serviceApi.admin.create(formData)
            : await serviceApi.create(formData)
        
        if (response.status === 200 || response.status === 201) {
            // Show success message
            const successMessage = response.data?.message || 'Service created successfully!'
            useAlert(successMessage, 'success')
            
            // Navigate back to services list
            router.push({ name: 'pages.admin.services' })
        } else {
            // Handle API response errors
            const message = response.data?.message || 'Failed to create service'
            useAlert(message, 'danger')
        }

    } catch (error) {
        console.error('Error creating service:', error)
        
        // Handle different types of errors
        if (error.response?.status === 422) {
            // Validation errors
            const errors = error.response.data?.errors || {}
            
            // Map backend errors to form errors
            Object.keys(errors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(serviceErrors, key)) {
                    serviceErrors[key] = errors[key][0] || errors[key]
                }
            })
            
            useAlert('Please correct the form errors', 'warning')
        } else if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to create services.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
            useAlert('Network error. Please check your connection and try again.', 'danger')
        } else {
            const message = error.response?.data?.message || 'Error creating service. Please try again.'
            useAlert(message, 'danger')
        }
    } finally {
        savingService.value = false
    }
}

// Initialize component
onMounted(async () => {
    await Promise.all([fetchCategories(), fetchCompanies()])
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 